import { Component, OnInit, OnDestroy, computed, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule, IconDefinition } from '@fortawesome/angular-fontawesome';
import { twMerge } from 'tailwind-merge';
import { SignalRService, ConnectionStatus } from 'src/app/@core/services/signalR.service';
import { TooltipDirective } from 'src/app/shared/directives/tooltip/tooltip.directive';


const base = [
  'fixed', 'inline-flex', 'items-center','justify-center', 'p-2', 'rounded-md', 'shadow-lg',
  'bg-white/80', 'text-black', 'w-auto', 'z-[99999]', 'drop-shadow-lg',
  'fa-duotone', 'w-6', 'h-6'
];

const iconBase = ['fa-duotone', 'drop-shadow-lg', 'text-xl'];

@Component({
  selector: 'connection-status-icon',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule],
  template: `<fa-icon [icon]="['fadr','signal-stream']" [class]="_iconClass()" aria-label="SignalR connection status" [ngStyle]="duotone()"/>`,
  host: { '[class]': '_class()' },
  hostDirectives:[
    {
      directive: TooltipDirective,
      inputs: ['appTooltip:tooltipText']
    }
  ]
})
export class ConnectionStatusIconComponent implements OnInit, OnDestroy {
  position     = input<'top' | 'bottom' | 'left' | 'right'>('top');
  userClasses  = input<string>('', { alias: 'classes' });
  animation    = input<'tailwind' | 'fa'>('tailwind');

  private readonly signalR = inject(SignalRService);


  private color = computed(() => ({
    connected:      '#22c55e',
    connecting:     '#facc15',
    reconnecting:   '#facc15',
    disconnected:   '#ef4444',
    error:          '#ef4444',
  }[this.signalR.slice.status()]));

  duotone = computed(() => ({
    '--fa-primary-opacity':   '1',
    '--fa-secondary-opacity': '0.25',
    '--fa-secondary-color':   '#ffffff',
    '--fa-primary-color':     this.color(),
  }) as Record<string, string>);

  pos = computed(() => ({
    top:    ['top-1', 'right-1'],
    bottom: ['bottom-1', 'right-1'],
    left:   ['left-1', 'top-1'],
    right:  ['right-1', 'top-1'],
  }[this.position()] ?? ['top-1', 'right-1']));

  tint = computed(() => ({
    connected:    ['text-emerald-500'],
    connecting:   ['text-yellow-400'],
    reconnecting: ['text-yellow-400'],
    disconnected: ['text-red-500'],
    error:        ['text-red-500'],
  }[this.signalR.slice.status()]));

  anim = computed(() => {
    const status =  this.signalR.slice.status()
    if (this.animation() === 'fa') {
      if (['connecting', 'reconnecting'].includes(status)) return ['fa-bounce', 'fa-fade'];
      if (['disconnected', 'error'].includes(status))     return ['fa-shake'];
      return [];
    }
    if (['connecting', 'reconnecting'].includes(status))   return ['animate-pulse'];
    if (['disconnected', 'error'].includes(status))        return ['animate-ping'];
    return [];
  });

  protected _class = computed(() =>
    twMerge([...base, ...this.pos(), ...this.tint(), ...this.userClasses().split(' ')].join(' '))
  );

  protected _iconClass = computed(() =>
    twMerge([...iconBase, ...this.tint(), ...this.anim()].join(' '))
  );

  ngOnInit() {  }
  ngOnDestroy() { }
}
